/**
 * BulkAssignModal — Asigna un bono o un descuento recurrente a varios empleados
 * (todos los activos o una selección). Si el empleado ya lo tiene, actualiza el
 * monto. Reutilizable vía `mode`.
 */
import { useMemo, useState } from 'react'
import { Modal } from '@/components/ds/Modal'
import { inputCls, labelCls } from '@/lib/formStyles'
import { extractErrorMessage } from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import {
  useEmployees, useBonuses,
  useBulkAssignBonus, useBulkAssignRecurringDeduction,
} from '../hooks/usePayroll'

interface Props {
  mode: 'bonus' | 'deduction'
  onClose: () => void
  // toast del padre (que renderiza el ToastContainer) — useToast es local.
  toast: { success: (m: string) => void; error: (m: string) => void }
}

export function BulkAssignModal({ mode, onClose, toast }: Props) {
  const { data: empsData } = useEmployees({ active: 'true', page_size: 9999 })
  const { data: bonusesData } = useBonuses()
  const bulkBonus = useBulkAssignBonus()
  const bulkDeduction = useBulkAssignRecurringDeduction()

  const employees: any[] = empsData?.employees || []
  const bonuses: any[] = (bonusesData as any)?.bonuses || (bonusesData as any) || []

  const [bonusId, setBonusId] = useState('')
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [target, setTarget] = useState<'all' | 'selected'>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  const filteredEmps = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return employees
    return employees.filter((e) =>
      (e.full_name || `${e.first_name} ${e.last_name}`).toLowerCase().includes(q) ||
      (e.rut || '').toLowerCase().includes(q),
    )
  }, [employees, search])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const isPending = bulkBonus.isPending || bulkDeduction.isPending

  const handleSubmit = async () => {
    if (mode === 'bonus' && !bonusId) { toast.error('Seleccionar el bono.'); return }
    if (mode === 'deduction' && !name.trim()) { toast.error('Indica el nombre del descuento.'); return }
    if (mode === 'bonus' && !amount) { toast.error('Indica el monto (o 0 para usar el del bono).'); return }
    if (mode === 'deduction' && !amount) { toast.error('Indica el monto.'); return }
    if (target === 'selected' && selected.size === 0) { toast.error('Seleccionar al menos un empleado.'); return }

    const payload: any = {
      amount,
      effective_from: from || null,
      effective_to: to || null,
    }
    if (target === 'all') payload.all_active = true
    else payload.employee_ids = Array.from(selected)
    if (mode === 'bonus') payload.bonus = bonusId
    else payload.name = name.trim()

    try {
      const r = mode === 'bonus'
        ? await bulkBonus.mutateAsync(payload)
        : await bulkDeduction.mutateAsync(payload)
      toast.success(`Asignado a ${r.total} empleado(s) — ${r.created} nuevo(s), ${r.updated} actualizado(s).`)
      onClose()
    } catch (e: any) {
      toast.error(extractErrorMessage(e, 'Error al asignar.'))
    }
  }

  const title = mode === 'bonus' ? 'Asignar bono a varios empleados' : 'Asignar descuento a varios empleados'

  return (
    <Modal open title={title} onClose={onClose} size="md"
      actions={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button onClick={handleSubmit} disabled={isPending}
            className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-50">
            {isPending ? 'Asignando…' : 'Asignar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {mode === 'bonus' ? (
            <SelectField
              dense
              label="Bono *"
              value={bonusId}
              onChange={(e) => setBonusId(e.target.value)}
            >
              <option value="">— Seleccionar —</option>
              {bonuses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </SelectField>
          ) : (
            <TextField
              dense
              label="Nombre del descuento *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seguro Complementario"
            />
          )}
          <div>
            <label htmlFor="bulkassignmodal-monto-mensual" className={labelCls}>Monto mensual *</label>
            <input id="bulkassignmodal-monto-mensual" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0" />
            {mode === 'bonus' && <p className="text-xs text-content-subtle mt-1">0 = usa el monto por defecto del bono.</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextField
            dense
            label="Vigente desde"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            inputProps={{ type: 'date' }}
          />
          <TextField
            dense
            label="Vigente hasta"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            inputProps={{ type: 'date' }}
          />
        </div>

        <div>
          <label className={labelCls}>Aplicar a</label>
          <div className="flex gap-4 text-sm text-content mb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={target === 'all'} onChange={() => setTarget('all')} />
              Todos los activos ({employees.length})
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={target === 'selected'} onChange={() => setTarget('selected')} />
              Seleccionar empleados {target === 'selected' && selected.size > 0 ? `(${selected.size})` : ''}
            </label>
          </div>

          {target === 'selected' && (
            <div className="border border-divider rounded-lg">
              <input aria-label="Buscar nombre o RUT"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nombre o RUT…"
                className="w-full px-3 py-2 text-sm bg-input-bg border-b border-divider rounded-t-lg focus:outline-none"
              />
              <div className="max-h-56 overflow-y-auto divide-y divide-divider">
                {filteredEmps.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-soft cursor-pointer">
                    <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                    <span className="text-content">{e.full_name || `${e.first_name} ${e.last_name}`}</span>
                    <span className="text-content-subtle font-mono text-xs ml-auto">{e.rut}</span>
                  </label>
                ))}
                {filteredEmps.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-content-subtle">Sin coincidencias.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-content-subtle">
          Si un empleado ya tiene este {mode === 'bonus' ? 'bono' : 'descuento'}, se actualiza el monto.
        </p>
      </div>
    </Modal>
  )
}
